import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
  Button,
} from "reactstrap";
import {
  createBusinessUser,
  updateBusinessUser,
} from "../../ApiCalls/businessUsers";
import "./styles/userFormModal.scss";

const emptyForm = {
  email: "",
  password: "",
  firstName: "",
  lastName: "",
  position: "",
  corporateEmail: "",
  personalEmail: "",
  cellPhone: "",
  role: "USER",
  permissions: [],
};

function UserFormModal({
  isOpen,
  toggle,
  user,
  mantisBusinessId,
  token,
  requesterId,
  permissions,
  onSave,
}) {
  const [formData, setFormData] = useState(emptyForm);

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || "",
        password: "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        position: user.position || "",
        corporateEmail: user.corporateEmail || "",
        personalEmail: user.personalEmail || "",
        cellPhone: user.cellPhone || "",
        role: user.mainUser ? "ADMIN" : user.supervisorUser ? "SUPERVISOR" : "USER",
        permissions: user.permissions?.map((p) => p.permission) || [],
      });
    } else {
      setFormData(emptyForm);
    }
    setErrors({});
    setSubmitError("");
  }, [user, isOpen]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Invalid email format";
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!user && !formData.password.trim()) {
      newErrors.password = "A password is required to create a user";
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handlePermissionToggle = (permissionCode) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permissionCode)
        ? prev.permissions.filter((p) => p !== permissionCode)
        : [...prev.permissions, permissionCode],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setSubmitError("");

      const selectedPermissions = permissions.filter((p) =>
        formData.permissions.includes(p.permission)
      );

      const userData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        position: formData.position,
        cellPhone: formData.cellPhone,
        personalEmail: formData.personalEmail,
        corporateEmail: formData.corporateEmail || formData.email,
        role: formData.role,
        permissions: selectedPermissions.map((p) => ({
          permission: p.permission,
          friendlyName: p.friendlyName,
        })),
      };

      if (formData.password) {
        userData.password = formData.password;
      }

      if (user) {
        await updateBusinessUser(
          token,
          requesterId,
          mantisBusinessId,
          user._id,
          userData
        );
      } else {
        await createBusinessUser(token, requesterId, mantisBusinessId, {
          ...userData,
          email: formData.email,
        });
      }

      onSave();
      toggle();
    } catch (err) {
      setSubmitError(err.message || "Failed to save user");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" className="user-form-modal">
      <ModalHeader toggle={toggle}>
        {user ? "Edit User" : "Create New User"}
      </ModalHeader>
      <ModalBody>
        {submitError && <Alert color="danger">{submitError}</Alert>}

        <Form>
          {/* Email */}
          <FormGroup>
            <Label for="email">
              Email <span className="required">*</span>
            </Label>
            <Input
              type="email"
              name="email"
              id="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder="user@example.com"
              invalid={!!errors.email}
              disabled={!!user}
            />
            {errors.email && (
              <small className="text-danger">{errors.email}</small>
            )}
          </FormGroup>

          {/* Password */}
          <FormGroup>
            <Label for="password">
              Password{" "}
              {user ? (
                <small className="text-muted">(leave blank to keep current)</small>
              ) : (
                <span className="required">*</span>
              )}
            </Label>
            <Input
              type="password"
              name="password"
              id="password"
              value={formData.password}
              onChange={handleInputChange}
              placeholder={user ? "••••••••" : "At least 8 characters"}
              invalid={!!errors.password}
              autoComplete="new-password"
            />
            {errors.password && (
              <small className="text-danger">{errors.password}</small>
            )}
          </FormGroup>

          {/* Name */}
          <div className="form-row">
            <FormGroup className="col-md-6">
              <Label for="firstName">
                First Name <span className="required">*</span>
              </Label>
              <Input
                type="text"
                name="firstName"
                id="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="John"
                invalid={!!errors.firstName}
              />
              {errors.firstName && (
                <small className="text-danger">{errors.firstName}</small>
              )}
            </FormGroup>

            <FormGroup className="col-md-6">
              <Label for="lastName">
                Last Name <span className="required">*</span>
              </Label>
              <Input
                type="text"
                name="lastName"
                id="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Doe"
                invalid={!!errors.lastName}
              />
              {errors.lastName && (
                <small className="text-danger">{errors.lastName}</small>
              )}
            </FormGroup>
          </div>

          {/* Contact Info */}
          <FormGroup>
            <Label for="position">Position</Label>
            <Input
              type="text"
              name="position"
              id="position"
              value={formData.position}
              onChange={handleInputChange}
              placeholder="e.g., Manager, Analyst"
            />
          </FormGroup>

          <div className="form-row">
            <FormGroup className="col-md-6">
              <Label for="corporateEmail">Corporate Email</Label>
              <Input
                type="email"
                name="corporateEmail"
                id="corporateEmail"
                value={formData.corporateEmail}
                onChange={handleInputChange}
                placeholder="corporate@company.com"
              />
            </FormGroup>

            <FormGroup className="col-md-6">
              <Label for="cellPhone">Cell Phone</Label>
              <Input
                type="tel"
                name="cellPhone"
                id="cellPhone"
                value={formData.cellPhone}
                onChange={handleInputChange}
                placeholder="+1 (555) 123-4567"
              />
            </FormGroup>
          </div>

          <FormGroup>
            <Label for="personalEmail">Personal Email</Label>
            <Input
              type="email"
              name="personalEmail"
              id="personalEmail"
              value={formData.personalEmail}
              onChange={handleInputChange}
              placeholder="personal@email.com"
            />
          </FormGroup>

          {/* Role Selection */}
          <div className="form-section">
            <h6 className="section-title">Role</h6>
            <div className="role-options">
              <div className="form-check">
                <input
                  type="radio"
                  name="role"
                  id="roleAdmin"
                  value="ADMIN"
                  checked={formData.role === "ADMIN"}
                  onChange={() =>
                    setFormData((prev) => ({ ...prev, role: "ADMIN" }))
                  }
                  className="form-check-input"
                />
                <label htmlFor="roleAdmin" className="form-check-label">
                  <strong>Admin</strong>
                  <small className="text-muted d-block">
                    Full access to all features
                  </small>
                </label>
              </div>

              <div className="form-check">
                <input
                  type="radio"
                  name="role"
                  id="roleSupervisor"
                  value="SUPERVISOR"
                  checked={formData.role === "SUPERVISOR"}
                  onChange={() =>
                    setFormData((prev) => ({ ...prev, role: "SUPERVISOR" }))
                  }
                  className="form-check-input"
                />
                <label htmlFor="roleSupervisor" className="form-check-label">
                  <strong>Supervisor</strong>
                  <small className="text-muted d-block">
                    Limited management capabilities
                  </small>
                </label>
              </div>

              <div className="form-check">
                <input
                  type="radio"
                  name="role"
                  id="roleUser"
                  value="USER"
                  checked={formData.role === "USER"}
                  onChange={() =>
                    setFormData((prev) => ({ ...prev, role: "USER" }))
                  }
                  className="form-check-input"
                />
                <label htmlFor="roleUser" className="form-check-label">
                  <strong>User</strong>
                  <small className="text-muted d-block">
                    Basic user permissions only
                  </small>
                </label>
              </div>
            </div>
          </div>

          {/* Permissions */}
          {permissions.length > 0 && (
            <div className="form-section">
              <h6 className="section-title">Permissions</h6>
              <p className="permissions-info text-muted">
                Select which features this user can access
              </p>
              <div className="permissions-grid">
                {permissions.map((perm) => (
                  <div key={perm._id} className="permission-item">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        id={`perm-${perm._id}`}
                        checked={formData.permissions.includes(perm.permission)}
                        onChange={() =>
                          handlePermissionToggle(perm.permission)
                        }
                        className="form-check-input"
                      />
                      <label
                        htmlFor={`perm-${perm._id}`}
                        className="form-check-label"
                      >
                        {perm.friendlyName}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={saving}>
          Cancel
        </Button>
        <Button
          color="primary"
          onClick={handleSubmit}
          disabled={saving}
        >
          {saving ? (
            <>
              <Spinner size="sm" /> Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save" /> {user ? "Update" : "Create"} User
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default UserFormModal;
